import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, BookOpen, Check, CheckCircle, Clock, Headphones, PenLine, X } from "lucide-react";
import { config } from "../config";

const apiUrl = config.apiUrl;

// ─── utils ────────────────────────────────────────────────────────────────────

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const questionNums = (test) => {
  if (test?.questions?.length)
    return test.questions.map((q) => q.number).sort((a, b) => a - b);
  const m = (test?.question_text || "").match(/\[\[(\d+)\]\]/g) || [];
  return [...new Set(m.map((x) => parseInt(x.replace(/[[\]]/g, ""))))].sort((a, b) => a - b);
};

const hasTemplate = (text) => /\[\[\d+\]\]|\[FLOW\]|\[BOX\]|\[GRID(?::|\])|\[TABLE(?::|\])/.test(text || "");

const templateQuestionNumbers = (text) => {
  const safeText = text || "";
  const numbers = new Set();
  [...safeText.matchAll(/\[\[(\d+)\]\]/g)].forEach((match) => numbers.add(Number(match[1])));
  [...safeText.matchAll(/^\s*(\d+)\s*\|/gm)].forEach((match) => numbers.add(Number(match[1])));
  return numbers;
};

const normalizeQuestionType = (type) => {
  if (type === "text") return "fill_blank";
  if (type === "sentence_completion") return "fill_blank";
  return type || "fill_blank";
};

const answerValue = (answers, key) => {
  const value = answers[String(key)] ?? answers[Number(key)] ?? "";
  return Array.isArray(value) ? value.join(" ") : String(value);
};

const calcScore = (questions, answers) => {
  const scorable = (questions || []).filter((q) => q.answer?.trim());
  if (!scorable.length) return null;
  const correct = scorable.filter(
    (q) => answerValue(answers, q.number).trim().toLowerCase() === q.answer.trim().toLowerCase()
  ).length;
  const answered = scorable.filter((q) => answerValue(answers, q.number).trim()).length;
  const wrong = answered - correct;
  return {
    correct,
    wrong,
    unanswered: scorable.length - answered,
    total: scorable.length,
    pct: Math.round((correct / scorable.length) * 100),
  };
};

// ─── template parser ──────────────────────────────────────────────────────────

const parseBlocks = (text) => {
  if (!text) return [];
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  let buf = [];

  const flush = () => {
    if (buf.length) { blocks.push({ type: "text", content: buf.join("\n") }); buf = []; }
  };

  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();

    if (t === "[FLOW]" || t === "[/FLOW]") {
      flush();
    } else if (t === "[BOX]") {
      flush();
      const boxLines = [];
      i++;
      while (i < lines.length) {
        const inner = lines[i].trim();
        if (inner === "[/BOX]") break; // explicit close
        // auto-close on next block-level tag
        if (inner === "↓" || inner === "→" || inner === "[BOX]" ||
          inner.startsWith("[GRID") || inner.startsWith("[TABLE") || inner === "[/FLOW]") {
          i--; // step back so outer i++ lands on this line next
          break;
        }
        boxLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: "box", content: boxLines.join("\n") });
    } else if (t === "↓" || t === "→") {
      flush();
      blocks.push({ type: "arrow", dir: t });
    } else if (t.startsWith("[GRID")) {
      flush();
      const colsMatch = t.match(/\[GRID:(.*?)\]/);
      const cols = colsMatch ? colsMatch[1].split(",").map((s) => s.trim()) : ["A", "B", "C", "D"];
      const rows = [];
      i++;
      while (i < lines.length && lines[i].trim() !== "[/GRID]") {
        const parts = lines[i].split("|");
        const num = parseInt(parts[0]?.trim());
        if (!isNaN(num)) rows.push({ number: num, label: parts.slice(1).join("|").trim() });
        i++;
      }
      blocks.push({ type: "grid", cols, rows });
    } else if (t.startsWith("[TABLE")) {
      flush();
      const hMatch = t.match(/\[TABLE:(.*?)\]/);
      const headers = hMatch ? hMatch[1].split("|").map((s) => s.trim()) : [];
      const rows = [];
      i++;
      while (i < lines.length && lines[i].trim() !== "[/TABLE]") {
        if (lines[i].trim()) rows.push(lines[i].split("|"));
        i++;
      }
      blocks.push({ type: "table", headers, rows });
    } else if (t.startsWith("[DRAGDROP]")) {
      flush();
      const rows = [];
      const optionsMap = [];
      let section = "rows";
      i++;
      while (i < lines.length && lines[i].trim() !== "[/DRAGDROP]") {
        const line = lines[i].trim();
        if (line === "--") { section = "options"; i++; continue; }
        if (section === "rows") {
          const parts = line.split("|");
          const num = parseInt(parts[0]?.trim());
          if (!isNaN(num)) rows.push({ number: num, label: parts.slice(1).join("|").trim() });
        } else {
          const parts = line.split("|");
          if (parts.length > 1) {
            optionsMap.push({ id: parts[0].trim(), text: parts.slice(1).join("|").trim() });
          }
        }
        i++;
      }
      blocks.push({ type: "dragdrop", rows, optionsMap });
    } else {
      buf.push(line);
    }
    i++;
  }
  flush();
  return blocks;
};

// ─── blank input ──────────────────────────────────────────────────────────────

const BlankInput = ({ number, answers, updateAnswer, questions, showAnswers, disabled }) => {
  const correct = questions?.find((q) => q.number === number)?.answer || "";
  const val = answerValue(answers, number);

  if (showAnswers) {
    const ok = correct && val.trim().toLowerCase() === correct.trim().toLowerCase();
    const hasAnswer = val.trim() !== "";
    return (
      <span className="inline-flex flex-col items-center mx-1 align-middle">
        <span className={`inline-block min-w-[88px] px-2 py-0.5 rounded text-center text-sm font-semibold border-2 ${!hasAnswer
          ? "border-gray-300 bg-gray-100 text-gray-400 italic"
          : ok
            ? "border-green-500 bg-green-100 text-green-800"
            : "border-red-400 bg-red-50 text-red-700 line-through"
          }`}>
          {val || "—"}
        </span>
        {!ok && correct && (
          <span className="text-[11px] font-bold text-green-700 mt-0.5 not-italic">{correct}</span>
        )}
        <sup className="text-[10px] text-gray-400">{number}</sup>
      </span>
    );
  }

  return (
    <span className="inline-flex items-baseline mx-1 align-middle">
      <input
        type="text"
        value={val}
        disabled={disabled}
        onChange={(e) => updateAnswer(number, e.target.value)}
        className="inline-block w-28 border-b-2 border-indigo-400 bg-transparent px-1 text-center text-sm font-medium focus:border-indigo-600 focus:outline-none transition-colors disabled:opacity-50"
        placeholder="..."
      />
      <sup className="ml-0.5 text-[10px] font-bold text-indigo-400">{number}</sup>
    </span>
  );
};

// ─── inline text ──────────────────────────────────────────────────────────────

const InlineText = ({ text, answers, updateAnswer, questions, showAnswers, disabled }) => {
  const segs = useMemo(() => {
    const result = [];
    const re = /\[\[(\d+)\]\]/g;
    let last = 0, m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) result.push({ t: "text", v: text.slice(last, m.index) });
      result.push({ t: "blank", n: parseInt(m[1]) });
      last = re.lastIndex;
    }
    if (last < text.length) result.push({ t: "text", v: text.slice(last) });
    return result;
  }, [text]);

  return (
    <>
      {segs.map((seg, i) =>
        seg.t === "blank" ? (
          <BlankInput key={i} number={seg.n} answers={answers} updateAnswer={updateAnswer}
            questions={questions} showAnswers={showAnswers} disabled={disabled} />
        ) : (
          seg.v.split("\n").map((line, li, arr) => (
            <React.Fragment key={`${i}-${li}`}>
              {line}{li < arr.length - 1 && <br />}
            </React.Fragment>
          ))
        )
      )}
    </>
  );
};

// ─── block components ─────────────────────────────────────────────────────────

const BoxBlock = (props) => (
  <div className="border-2 border-gray-400 rounded-lg px-6 py-4 bg-white text-[15px] leading-8 mx-auto max-w-xl text-center shadow-sm my-1">
    <InlineText text={props.content} {...props} />
  </div>
);

const ArrowDivider = ({ dir }) => (
  <div className="flex justify-center my-1 text-gray-500 text-xl select-none">
    {dir === "→" ? "▶" : "▼"}
  </div>
);

const GridMCQ = ({ cols, rows, answers, updateAnswer, questions, showAnswers, disabled }) => (
  <div className="overflow-x-auto my-3 rounded-xl border border-gray-200 shadow-sm">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-indigo-50">
          <th className="px-4 py-3 text-left font-semibold text-gray-600 min-w-[200px]">Question</th>
          {cols.map((col) => (
            <th key={col} className="px-3 py-3 text-center font-bold text-indigo-700 w-14">{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => {
          const correct = questions?.find((q) => q.number === row.number)?.answer;
          const selected = answerValue(answers, row.number);
          return (
            <tr key={row.number} className={`border-t border-gray-100 transition-colors ${ri % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded border border-gray-400 text-xs font-bold text-gray-600 shrink-0">
                    {row.number}
                  </span>
                  <span className="text-gray-800 font-medium">{row.label}</span>
                </span>
              </td>
              {cols.map((col) => {
                const isSel = selected === col;
                const isCorrect = showAnswers && col === correct;
                const isWrong = showAnswers && isSel && col !== correct;
                return (
                  <td key={col} className="px-3 py-3 text-center">
                    <button
                      type="button"
                      disabled={disabled || showAnswers}
                      onClick={() => updateAnswer(row.number, col)}
                      className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all font-bold text-sm ${isCorrect
                        ? "border-green-500 bg-green-500 text-white shadow"
                        : isWrong
                          ? "border-red-400 bg-red-400 text-white"
                          : isSel
                            ? "border-indigo-600 bg-indigo-600 text-white shadow"
                            : "border-gray-300 bg-white text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                        }`}
                    >
                      {isCorrect ? <Check className="h-4 w-4" /> : isWrong ? <X className="h-4 w-4" /> : isSel ? col : ""}
                    </button>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const InlineTable = (props) => (
  <div className="overflow-x-auto my-3 rounded-xl border border-gray-200 shadow-sm">
    <table className="w-full border-collapse text-sm">
      {props.headers.length > 0 && (
        <thead>
          <tr className="bg-gray-100">
            {props.headers.map((h, i) => (
              <th key={i} className="border-b border-gray-200 px-4 py-3 text-left font-bold text-gray-700">{h}</th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {props.rows.map((row, ri) => (
          <tr key={ri} className={`border-t border-gray-100 ${ri % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
            {row.map((cell, ci) => (
              <td key={ci} className="px-4 py-3 text-[14px] leading-8 align-top text-gray-800">
                <InlineText text={cell.trim()} {...props} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DragDropMatching = ({ rows, optionsMap, answers, updateAnswer, questions, showAnswers, disabled }) => {
  const handleDragStart = (e, optionId) => {
    e.dataTransfer.setData("text/plain", optionId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, rowNumber) => {
    e.preventDefault();
    if (disabled || showAnswers) return;
    const optionId = e.dataTransfer.getData("text/plain");
    if (optionId) {
      updateAnswer(rowNumber, optionId);
    }
  };

  // Find which options have already been used
  const usedOptions = new Set(
    rows.map(r => answerValue(answers, r.number)).filter(Boolean)
  );

  return (
    <div className="my-5 border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <div className="bg-indigo-50/50 px-5 py-4 border-b border-gray-200">
        <p className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Drag items to answer</p>
        <div className="flex flex-wrap gap-2">
          {optionsMap.map((opt) => {
            const isUsed = usedOptions.has(opt.id);
            return (
              <div
                key={opt.id}
                draggable={!disabled && !showAnswers && !isUsed}
                onDragStart={(e) => handleDragStart(e, opt.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${isUsed
                  ? "opacity-50 border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                  : "border-indigo-300 bg-white text-indigo-700 shadow-sm cursor-grab hover:border-indigo-500 hover:shadow"
                  }`}
              >
                <span className="font-black text-indigo-500">{opt.id}</span>
                {opt.text}
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-5 space-y-2">
        {rows.map((row) => {
          const selectedId = answerValue(answers, row.number);
          const selectedOpt = optionsMap.find(o => o.id === selectedId);
          const correctId = questions?.find(q => q.number === row.number)?.answer;

          const isCorrect = showAnswers && selectedId === correctId;
          const isWrong = showAnswers && selectedId && selectedId !== correctId;

          return (
            <div key={row.number} className="flex flex-col md:flex-row md:items-center gap-3 py-2 border-b border-gray-100 last:border-0">
              <div className="flex-1 text-[15px] font-medium text-gray-800 flex items-center gap-3">
                <span className="w-8 text-right font-black text-gray-400 shrink-0">{row.number}</span>
                {row.label}
              </div>
              <div
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, row.number)}
                className={`w-full md:w-80 shrink-0 min-h-[44px] rounded-lg border-2 border-dashed flex items-center p-1 transition-all ${isCorrect ? "border-green-400 bg-green-50" :
                  isWrong ? "border-red-400 bg-red-50" :
                    selectedOpt ? "border-indigo-400 bg-indigo-50" :
                      disabled ? "border-gray-200 bg-gray-50" :
                        "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
                  }`}
              >
                {selectedOpt ? (
                  <div className="w-full flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-1.5 shadow-sm text-sm">
                    <div className="flex items-center gap-2 truncate">
                      <span className="font-black text-indigo-600">{selectedOpt.id}</span>
                      <span className="text-gray-700 truncate" title={selectedOpt.text}>{selectedOpt.text}</span>
                    </div>
                    {(!disabled && !showAnswers) && (
                      <button
                        onClick={() => updateAnswer(row.number, "")}
                        className="text-gray-400 hover:text-red-500 shrink-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 w-full text-center">Drop answer here</span>
                )}
                {showAnswers && (
                  <div className="ml-2 shrink-0">
                    {isCorrect ? <Check className="text-green-600 w-5 h-5" /> :
                      isWrong ? <X className="text-red-600 w-5 h-5" /> : null}
                  </div>
                )}
              </div>
              {showAnswers && isWrong && correctId && (
                <div className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded">
                  Correct: {correctId}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── template renderer ────────────────────────────────────────────────────────

const TemplateRenderer = ({ text, answers, updateAnswer, questions, showAnswers, disabled }) => {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  const shared = { answers, updateAnswer, questions, showAnswers, disabled };

  return (
    <div className="text-[15px] leading-9 text-gray-900">
      {blocks.map((block, i) => {
        if (block.type === "box") return <BoxBlock key={i} content={block.content} {...shared} />;
        if (block.type === "arrow") return <ArrowDivider key={i} dir={block.dir} />;
        if (block.type === "grid") return <GridMCQ key={i} cols={block.cols} rows={block.rows} {...shared} />;
        if (block.type === "table") return <InlineTable key={i} headers={block.headers} rows={block.rows} {...shared} />;
        if (block.type === "dragdrop") return <DragDropMatching key={i} rows={block.rows} optionsMap={block.optionsMap} {...shared} />;
        return <div key={i}><InlineText text={block.content} {...shared} /></div>;
      })}
    </div>
  );
};

// ─── passage renderer ────────────────────────────────────────────────────────

const renderPassage = (text = "") => {
  const parts = [];
  const pattern = /\[\[(.*?)\|(\d+)\]\]/g;
  let lastIndex = 0, match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push({ text: match[1], number: match[2] });
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.map((part, i) =>
    typeof part === "string"
      ? part.split("\n").map((line, li, arr) => (
        <React.Fragment key={`${i}-${li}`}>
          {line}{li < arr.length - 1 && <br />}
        </React.Fragment>
      ))
      : (
        <span key={i} className="rounded bg-lime-200 px-1 font-medium">
          {part.text}
          <span className="ml-1 rounded bg-green-700 px-1 py-0.5 text-[11px] font-bold text-white">{part.number}</span>
        </span>
      )
  );
};

// ─── score banner ──────────────────────────────────────────────────────────────

const ScoreBanner = ({ score }) => {
  if (!score) return null;
  const color = score.pct >= 70 ? "bg-green-50 border-green-300 text-green-800"
    : score.pct >= 50 ? "bg-amber-50 border-amber-300 text-amber-800"
      : "bg-red-50 border-red-300 text-red-800";
  const ring = score.pct >= 70 ? "bg-green-500" : score.pct >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className={`flex flex-wrap items-center gap-4 rounded-xl border-2 px-5 py-3 mb-5 ${color}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-full ${ring} flex items-center justify-center text-white font-black text-sm shrink-0`}>
          {score.pct}%
        </div>
        <div>
          <p className="font-black text-base">Your Score</p>
          <p className="text-sm opacity-80">{score.correct} correct out of {score.total} questions</p>
        </div>
      </div>
      <div className="flex gap-3 ml-auto flex-wrap">
        <Pill icon={Check} label={`${score.correct} Correct`} color="bg-green-100 text-green-700 border-green-200" />
        <Pill icon={X} label={`${score.wrong} Wrong`} color="bg-red-100 text-red-700 border-red-200" />
        {score.unanswered > 0 && (
          <Pill icon={null} label={`${score.unanswered} Skipped`} color="bg-gray-100 text-gray-600 border-gray-200" />
        )}
      </div>
    </div>
  );
};

const Pill = ({ icon: Icon, label, color }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${color}`}>
    {Icon && <Icon className="h-3 w-3" />}
    {label}
  </span>
);

// ─── question panel ────────────────────────────────────────────────────────────

const QuestionPanel = ({ test, answers, updateAnswer, showAnswers, isTimeUp }) => {
  const usesTpl = hasTemplate(test.question_text);
  const tplNumbers = useMemo(() => templateQuestionNumbers(test.question_text), [test.question_text]);
  const cardQuestions = useMemo(
    () => (test.questions || [])
      .filter((q) => !usesTpl || !tplNumbers.has(Number(q.number)) || q.render_outside_template)
      .sort((a, b) => Number(a.number || 0) - Number(b.number || 0)),
    [test.questions, usesTpl, tplNumbers]
  );

  const groupedQuestions = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    cardQuestions.forEach((q) => {
      const qType = normalizeQuestionType(q.type);
      const isMcq = qType === "mcq" || qType === "true_false";
      const opts = q.options?.length ? q.options : (qType === "true_false" ? ["TRUE", "FALSE", "NOT GIVEN"] : []);

      if (isMcq && currentGroup && currentGroup.prompt === q.prompt && JSON.stringify(currentGroup.options) === JSON.stringify(opts)) {
        currentGroup.questions.push(q);
        currentGroup.isMcqMulti = true;
      } else {
        currentGroup = {
          id: q.number,
          prompt: q.prompt,
          options: opts,
          questions: [q],
          isMcqMulti: false,
          qType,
          q
        };
        groups.push(currentGroup);
      }
    });

    return groups;
  }, [cardQuestions]);

  const shared = { answers, updateAnswer, questions: test.questions, showAnswers, disabled: isTimeUp || showAnswers };
  const score = showAnswers ? calcScore(test.questions, answers) : null;
  const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

  return (
    <div className="space-y-5">
      {showAnswers && <ScoreBanner score={score} />}

      {test.question_title && (
        <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-200 pb-2">
          {test.question_title}
        </h3>
      )}

      <div className="space-y-5">
        {usesTpl && (
          <TemplateRenderer text={test.question_text} {...shared} />
        )}

        {groupedQuestions.map((group) => {
          if (group.isMcqMulti) {
            const qs = group.questions;
            const title = qs.length > 1 ? `${qs[0].number}-${qs[qs.length - 1].number}` : qs[0].number;
            const selectedOpts = qs.map(q => ({ number: q.number, val: answerValue(answers, q.number) }));
            const selectedValues = selectedOpts.map(s => s.val).filter(Boolean);

            const toggleOption = (opt) => {
              const existing = selectedOpts.find(s => s.val === opt);
              if (existing) {
                updateAnswer(existing.number, "");
              } else {
                const empty = selectedOpts.find(s => !s.val);
                if (empty) {
                  updateAnswer(empty.number, opt);
                }
              }
            };

            return (
              <div key={group.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex gap-3 items-start px-4 pt-4 pb-3">
                  <span className="flex h-7 min-w-[28px] px-1 shrink-0 items-center justify-center rounded border-2 border-gray-800 text-xs font-black text-gray-900">
                    {title}
                  </span>
                  <p className="text-[15px] leading-6 text-gray-900 font-medium pt-0.5">{group.prompt}</p>
                </div>
                <div className="px-4 pb-4 space-y-1.5">
                  {group.options.map((opt, oi) => {
                    const letter = LETTERS[oi] || String(oi + 1);
                    const isSel = selectedValues.includes(opt);
                    const correctAnswers = qs.map(q => q.answer).filter(Boolean);
                    const isCorrectAnswer = correctAnswers.includes(opt);

                    const optCorrect = showAnswers && isSel && isCorrectAnswer;
                    const optWrong = showAnswers && isSel && !isCorrectAnswer;
                    const optMissed = showAnswers && !isSel && isCorrectAnswer;

                    return (
                      <button
                        key={opt}
                        type="button"
                        disabled={isTimeUp || showAnswers}
                        onClick={() => toggleOption(opt)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 text-left transition-all font-medium text-sm ${(optCorrect || optMissed)
                          ? "border-green-500 bg-green-50 text-green-800"
                          : optWrong
                            ? "border-red-400 bg-red-50 text-red-700"
                            : isSel
                              ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
                          }`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-black border-2 transition-colors ${(optCorrect || optMissed)
                          ? "border-green-500 bg-green-500 text-white"
                          : optWrong
                            ? "border-red-400 bg-red-400 text-white"
                            : isSel
                              ? "border-indigo-600 bg-indigo-600 text-white"
                              : "border-gray-300 text-gray-500"
                          }`}>
                          {(optCorrect || optMissed) ? <Check className="h-3.5 w-3.5" /> : optWrong ? <X className="h-3.5 w-3.5" /> : letter}
                        </span>
                        <span className="flex-1">{opt}</span>
                        {(optCorrect || optMissed) && <Check className="h-4 w-4 text-green-500 shrink-0" />}
                        {optWrong && <X className="h-4 w-4 text-red-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          const q = group.q;
          const questionType = group.qType;
          const isMcq = questionType === "mcq" || questionType === "true_false";
          const isMatching = questionType === "matching";
          const isChoiceType = isMcq || isMatching;
          const opts = group.options;
          const sel = answerValue(answers, q.number);
          const isCorrect = showAnswers && sel && sel === q.answer;
          const isWrong = showAnswers && sel && sel !== q.answer;

          return (
            <div key={q.number} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Question row */}
              <div className="flex gap-3 items-start px-4 pt-4 pb-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border-2 border-gray-800 text-xs font-black text-gray-900">
                  {q.number}
                </span>
                <p className="text-[15px] leading-6 text-gray-900 font-medium pt-0.5">{q.prompt}</p>
              </div>

              {/* Options */}
              {isChoiceType ? (
                <div className="px-4 pb-4 space-y-1.5">
                  {opts.map((opt, oi) => {
                    const letter = LETTERS[oi] || String(oi + 1);
                    const isSel = sel === opt;
                    const optCorrect = showAnswers && opt === q.answer;
                    const optWrong = showAnswers && isSel && opt !== q.answer;
                    return (
                      <button
                        key={opt}
                        type="button"
                        disabled={isTimeUp || showAnswers}
                        onClick={() => updateAnswer(q.number, opt)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 text-left transition-all font-medium text-sm ${optCorrect
                          ? "border-green-500 bg-green-50 text-green-800"
                          : optWrong
                            ? "border-red-400 bg-red-50 text-red-700"
                            : isSel
                              ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                              : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/40"
                          }`}
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-black border-2 transition-colors ${optCorrect
                          ? "border-green-500 bg-green-500 text-white"
                          : optWrong
                            ? "border-red-400 bg-red-400 text-white"
                            : isSel
                              ? "border-indigo-600 bg-indigo-600 text-white"
                              : "border-gray-300 text-gray-500"
                          }`}>
                          {optCorrect ? <Check className="h-3.5 w-3.5" /> : optWrong ? <X className="h-3.5 w-3.5" /> : letter}
                        </span>
                        <span className="flex-1">{opt}</span>
                        {optCorrect && <Check className="h-4 w-4 text-green-500 shrink-0" />}
                        {optWrong && <X className="h-4 w-4 text-red-400 shrink-0" />}
                      </button>
                    );
                  })}
                  {isMatching && !opts.length && (
                    <input
                      value={sel || ""}
                      onChange={(e) => updateAnswer(q.number, e.target.value)}
                      disabled={isTimeUp || showAnswers}
                      className="w-full max-w-xs rounded-lg border-2 border-indigo-200 bg-white px-3 py-2 text-sm font-medium focus:border-indigo-500 focus:outline-none disabled:opacity-60"
                      placeholder="Type the matching option..."
                    />
                  )}
                  {/* Show correct answer if student picked wrong */}
                  {showAnswers && isWrong && q.answer && (
                    <p className="flex items-center gap-1.5 text-xs font-bold text-green-700 mt-1 pl-1">
                      <CheckCircle className="h-3.5 w-3.5" /> Correct answer: {q.answer}
                    </p>
                  )}
                </div>
              ) : (
                <div className="px-4 pb-4 pl-14 space-y-2">
                  <input
                    value={sel || ""}
                    onChange={(e) => updateAnswer(q.number, e.target.value)}
                    disabled={isTimeUp || showAnswers}
                    className={`w-full max-w-xs rounded-lg border-2 px-3 py-2 text-sm font-medium focus:outline-none transition-colors ${showAnswers
                      ? isCorrect
                        ? "border-green-500 bg-green-50 text-green-800"
                        : isWrong
                          ? "border-red-400 bg-red-50 text-red-700"
                          : "border-gray-200 bg-gray-50 text-gray-400"
                      : "border-indigo-200 bg-white focus:border-indigo-500"
                      }`}
                    placeholder="Type your answer..."
                  />
                  {showAnswers && (
                    <p className={`flex items-center gap-1.5 text-xs font-bold ${isCorrect ? "text-green-700" : "text-red-600"}`}>
                      {isCorrect
                        ? <><Check className="h-3.5 w-3.5" /> Correct!</>
                        : <><X className="h-3.5 w-3.5" /> Correct answer: <span className="text-green-700">{q.answer || "—"}</span></>
                      }
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── writing panel ────────────────────────────────────────────────────────────

const WritingPanel = ({ test, writingText, setWritingText, isTimeUp, submitting, submitWriting, wordCount }) => (
  <div className="flex flex-col gap-5">
    <div className="relative">
      <textarea
        value={writingText}
        onChange={(e) => setWritingText(e.target.value)}
        disabled={isTimeUp || submitting}
        rows={14}
        className={`w-full rounded-2xl border-2 p-5 text-base outline-none transition-all resize-none leading-7 ${isTimeUp
          ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-white border-indigo-100 focus:border-indigo-500 shadow-sm"
          }`}
        placeholder={isTimeUp ? "Time is up!" : "Start writing your response here…"}
      />
      {isTimeUp && (
        <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/5">
          <span className="bg-red-600 text-white px-6 py-2 rounded-full font-black shadow-lg text-sm">
            Time's Up!
          </span>
        </div>
      )}
    </div>

    <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-200 px-5 py-4 shadow-sm">
      <div className="flex gap-6">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Words</p>
          <p className="text-2xl font-black text-gray-900">{wordCount}</p>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Status</p>
          <p className={`text-sm font-bold ${isTimeUp ? "text-red-600" : "text-green-600"}`}>
            {isTimeUp ? "Finished" : "Writing…"}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ─── bottom part navigation ───────────────────────────────────────────────────

const BottomPartNav = ({ tests, activeTestId, switchPart, answers }) => (
  <div className="fixed bottom-0 inset-x-0 z-20 bg-white border-t-2 border-gray-200 shadow-2xl">
    <div className="flex overflow-x-auto">
      {tests.map((test, pi) => {
        const isActive = String(test.id) === String(activeTestId);
        const nums = questionNums(test);
        const answered = nums.filter((n) => answerValue(answers, n).trim()).length;
        const pct = nums.length ? Math.round((answered / nums.length) * 100) : 0;

        return (
          <div
            key={test.id}
            className={`flex flex-col min-w-[100px] border-r border-gray-100 transition-colors ${isActive ? "bg-indigo-50" : "bg-white hover:bg-gray-50"
              }`}
          >
            <button
              onClick={() => switchPart(test)}
              className={`px-4 pt-2.5 pb-1 text-xs font-extrabold uppercase tracking-wide transition-colors text-left ${isActive ? "text-indigo-700" : "text-gray-500"
                }`}
            >
              Part {pi + 1}
            </button>

            {isActive ? (
              <div className="flex flex-wrap gap-1 px-3 pb-2.5 pt-1 max-w-[260px]">
                {nums.map((n) => (
                  <span key={n} className={`flex h-6 w-6 items-center justify-center rounded text-[11px] font-bold transition-colors ${answerValue(answers, n).trim()
                    ? "bg-indigo-600 text-white"
                    : "border-2 border-gray-300 bg-white text-gray-500"
                    }`}>{n}</span>
                ))}
              </div>
            ) : (
              <div className="px-3 pb-2.5 pt-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] text-gray-500 font-medium">{answered}/{nums.length}</span>
                  {pct === 100 && <Check className="h-3 w-3 text-green-500" />}
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : "bg-indigo-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// ─── score result modal ───────────────────────────────────────────────────────

const getBandScore = (correct, total) => {
  if (total <= 0) return 0;
  let scaled = correct;
  if (total !== 40) scaled = Math.round((correct / total) * 40);
  if (scaled >= 39) return 9.0;
  if (scaled >= 37) return 8.5;
  if (scaled >= 35) return 8.0;
  if (scaled >= 32) return 7.5;
  if (scaled >= 30) return 7.0;
  if (scaled >= 26) return 6.5;
  if (scaled >= 23) return 6.0;
  if (scaled >= 18) return 5.5;
  if (scaled >= 15) return 5.0;
  if (scaled >= 13) return 4.5;
  if (scaled >= 10) return 4.0;
  if (scaled >= 6) return 3.5;
  if (scaled >= 4) return 3.0;
  if (scaled >= 2) return 2.5;
  return 0.0;
};

const ScoreModal = ({ score, onClose, onReview }) => {
  if (!score) return null;
  const ring = score.pct >= 70 ? "from-green-400 to-emerald-500" : score.pct >= 50 ? "from-amber-400 to-orange-500" : "from-red-400 to-rose-500";
  const label = score.pct >= 70 ? "Excellent!" : score.pct >= 50 ? "Good Job!" : "Keep Practicing!";
  const band = getBandScore(score.correct, score.total);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden">
        {/* Top gradient band */}
        <div className={`bg-gradient-to-r ${ring} px-6 pt-8 pb-6 text-center`}>
          <p className="text-white/80 text-sm font-bold uppercase tracking-widest mb-1">Test Complete</p>
          <div className="flex items-center justify-center gap-4 mb-2">
            <div>
              <p className="text-5xl font-black text-white">{score.pct}%</p>
              <p className="text-[10px] uppercase font-bold text-white/80">Percentage</p>
            </div>
            <div className="h-12 w-px bg-white/30"></div>
            <div>
              <p className="text-5xl font-black text-white">{band.toFixed(1)}</p>
              <p className="text-[10px] uppercase font-bold text-white/80">Est. Band</p>
            </div>
          </div>
          <p className="text-white font-bold text-lg mt-2">{label}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Score breakdown */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-green-50 rounded-2xl p-3 border border-green-100">
              <p className="text-2xl font-black text-green-600">{score.correct}</p>
              <p className="text-[11px] font-bold uppercase text-green-700 mt-0.5">Correct</p>
            </div>
            <div className="bg-red-50 rounded-2xl p-3 border border-red-100">
              <p className="text-2xl font-black text-red-500">{score.wrong}</p>
              <p className="text-[11px] font-bold uppercase text-red-600 mt-0.5">Wrong</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3 border border-gray-100">
              <p className="text-2xl font-black text-gray-600">{score.unanswered}</p>
              <p className="text-[11px] font-bold uppercase text-gray-500 mt-0.5">Skipped</p>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            {score.correct} out of {score.total} questions answered correctly
          </p>

          <button
            onClick={onReview}
            className="w-full bg-gray-900 text-white font-bold py-3 rounded-xl hover:bg-black transition-colors text-sm"
          >
            Review Answers
          </button>
          <button
            onClick={onClose}
            className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const IeltsPractice = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [activeSection, setActiveSection] = useState("listening");
  const [activeTestId, setActiveTestId] = useState("");
  const [answers, setAnswers] = useState({});
  const [writingTexts, setWritingTexts] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);

  const [examLocked, setExamLocked] = useState(false);
  const [activePartIndex, setActivePartIndex] = useState(0);

  // Handle checking answers: show answers and score modal
  const handleCheckAnswers = () => {
    setShowAnswers(true);
    setShowScoreModal(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const h = token ? { Authorization: `Bearer ${token}` } : {};
        const [sRes, uRes] = await Promise.all([
          axios.get(`${apiUrl}/ielts-subjects`),
          token ? axios.get(`${apiUrl}/user`, { headers: h }) : Promise.resolve({ data: null }),
        ]);
        const active = (sRes.data || []).filter((s) => s.is_active);
        setSubjects(active);
        if (active.length) {
          setSelectedSubjectId(String(active[0].id));
        }
        setUser(uRes.data);
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSubjectId || !user) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    axios.get(`${apiUrl}/ielts-results/check-lock/${selectedSubjectId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setExamLocked(res.data.is_locked);
    }).catch(err => console.error("Check lock failed:", err));
  }, [selectedSubjectId, user]);

  const selectedSubject = subjects.find((s) => String(s.id) === String(selectedSubjectId));
  const tests = useMemo(
    () =>
      (selectedSubject?.tests || [])
        .filter((t) => t.section === activeSection && t.is_published)
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [selectedSubject, activeSection]
  );

  useEffect(() => {
    if (!tests.length) return;
    const first = tests[0];
    setActiveTestId(String(first.id));
    setActivePartIndex(0);
    setAnswers({});
    // Don't reset writingTexts here — it's persisted per subject
    setShowAnswers(false);
    setShowScoreModal(false);
    setIsTimeUp(false);
    setShowTimeUpModal(false);
    const totalTime = tests.reduce((sum, t) => sum + (t.time_limit || 20), 0);
    setTimeRemaining(totalTime * 60);
  }, [tests]);

  useEffect(() => {
    if (!timeRemaining || isTimeUp) return;
    const id = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) { clearInterval(id); setIsTimeUp(true); setShowTimeUpModal(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timeRemaining, isTimeUp, activeTestId]);

  const storageKey = tests.length ? `ielts_answers_${selectedSubjectId}_${activeSection}` : "";
  const updateAnswer = (n, v) => setAnswers((p) => ({ ...p, [String(n)]: v }));

  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    setAnswers(saved ? JSON.parse(saved) : {});
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || showAnswers) return;
    localStorage.setItem(storageKey, JSON.stringify(answers));
  }, [answers, storageKey, showAnswers]);

  // Persist writing texts across section switches
  const writingStorageKey = `ielts_writing_${selectedSubjectId}`;

  useEffect(() => {
    if (!selectedSubjectId) return;
    const saved = localStorage.getItem(writingStorageKey);
    if (saved) {
      try { setWritingTexts(JSON.parse(saved)); } catch {}
    }
  }, [selectedSubjectId]);

  useEffect(() => {
    localStorage.setItem(writingStorageKey, JSON.stringify(writingTexts));
  }, [writingTexts, writingStorageKey]);

  const switchPart = (test) => {
    setActiveTestId(String(test.id));
    const el = document.getElementById(`part-${test.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFinalSubmit = async () => {
    if (!selectedSubjectId || !user) {
      alert("Please log in to submit.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");

      // Submit listening
      const listeningAnswers = JSON.parse(localStorage.getItem(`ielts_answers_${selectedSubjectId}_listening`) || "{}");
      const listeningRes = await axios.post(`${apiUrl}/ielts-results/submit`, {
        ielts_subject_id: selectedSubjectId,
        section: "listening",
        answers: listeningAnswers,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Submit reading
      const readingAnswers = JSON.parse(localStorage.getItem(`ielts_answers_${selectedSubjectId}_reading`) || "{}");
      const readingRes = await axios.post(`${apiUrl}/ielts-results/submit`, {
        ielts_subject_id: selectedSubjectId,
        section: "reading",
        answers: readingAnswers,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Submit writing submissions if any
      await Promise.all(Object.keys(writingTexts).map(testId => {
        const text = writingTexts[testId] || "";
        if (!text.trim()) return Promise.resolve();
        return axios.post(
          `${apiUrl}/ielts-writing-submissions`,
          { ielts_test_id: testId, content: text },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }));

      // Lock the exam after all sections done
      await axios.post(`${apiUrl}/ielts-results/finalize`, {
        ielts_subject_id: selectedSubjectId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.removeItem(`ielts_answers_${selectedSubjectId}_listening`);
      localStorage.removeItem(`ielts_answers_${selectedSubjectId}_reading`);
      localStorage.removeItem(`ielts_writing_${selectedSubjectId}`);

      window.location.href = "/ielts-result";
    } catch (e) {
      alert(e.response?.data?.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (examLocked) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md text-center">
        <div className="h-16 w-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-2">Exam Locked</h2>
        <p className="text-gray-600 mb-6">You have already completed this exam. It is locked to prevent re-attempts.</p>
        <a href="/ielts-result" className="inline-block bg-indigo-600 text-white font-bold px-6 py-3 rounded-full hover:bg-indigo-700 transition-colors">
          View My Results
        </a>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
        <p className="text-gray-500 font-medium">Loading practice…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <p className="text-red-600 font-medium">{error}</p>
    </div>
  );

  const isReading = activeSection === "reading";
  const isWriting = activeSection === "writing";
  const activeScore = tests.length ? calcScore(tests.flatMap(t => t.questions || []), answers) : null;

  const SECTION_TABS = [
    { key: "listening", label: "Listening", Icon: Headphones },
    { key: "reading", label: "Reading", Icon: BookOpen },
    { key: "writing", label: "Writing", Icon: PenLine },
  ];

  return (
    <div className="h-screen bg-[#f5f7fa] flex flex-col overflow-hidden">

      {/* ── Header ── */}
      <header className="shrink-0 z-10 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between gap-3 px-4 h-14 max-w-7xl mx-auto">

          {/* Brand */}
          <a href="/" className="shrink-0 flex items-center gap-2">
            {(localStorage.getItem("sidebarLogoImg") || localStorage.getItem("ieltsLogoImg")) ? (
              <img
                src={localStorage.getItem("sidebarLogoImg") || localStorage.getItem("ieltsLogoImg")}
                alt=""
                className="h-8 w-8 object-contain rounded"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-white text-sm">
                {localStorage.getItem("sidebarLogoText") || localStorage.getItem("ieltsLogoText") || "A"}
              </div>
            )}
          </a>

          {/* Timer */}
          {tests.length > 0 && (
            <div className={`inline-flex items-center gap-1.5 font-mono font-black text-sm px-3 py-1.5 rounded-full border-2 shrink-0 ${timeRemaining < 60
              ? "border-red-500 bg-red-50 text-red-600 animate-pulse"
              : timeRemaining < 300
                ? "border-orange-400 bg-orange-50 text-orange-600"
                : "border-indigo-300 bg-indigo-50 text-indigo-700"
              }`}>
              <Clock className="h-3.5 w-3.5" />
              {fmt(timeRemaining)}
            </div>
          )}

          {/* Section tabs + subject select + check answers */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            <select
              value={selectedSubjectId}
              onChange={(e) => setSelectedSubjectId(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 shrink-0 focus:outline-none focus:border-indigo-400"
            >
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
              {SECTION_TABS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold capitalize transition-colors ${activeSection === key
                    ? "bg-gray-900 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  <Icon className="h-3.5 w-3.5" />{label}
                </button>
              ))}
            </div>

            {/* Move to next section instead of submitting */}
            {!isWriting && tests.length > 0 && (
              <button
                onClick={() => setActiveSection(activeSection === 'listening' ? 'reading' : 'writing')}
                disabled={submitting}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700 transition-colors shadow-sm"
              >
                <CheckCircle className="h-3.5 w-3.5" /> {activeSection === 'listening' ? 'Go to Reading' : 'Go to Writing'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      {!user ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="bg-white border border-gray-200 rounded-3xl p-10 max-w-md text-center shadow-lg">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PenLine className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">Login Required</h2>
            <p className="text-gray-500 mb-6 text-sm">Please log in to take IELTS practice tests.</p>
            <a href="/login" className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-sm">
              Go to Login
            </a>
          </div>
        </div>

      ) : !subjects.length ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 font-medium">
          No IELTS content published yet.
        </div>

      ) : !tests.length ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 font-medium">
          No published {activeSection} practice found.
        </div>

      ) : (
        <>
          <main className={`flex-1 overflow-auto ${isWriting ? "" : "pb-32"}`}>
            {tests.length > 0 && (() => {
              const test = tests[activePartIndex];
              if (!test) return null;
              const partLabel = `Part ${activePartIndex + 1}`;
              const text = writingTexts[test.id] || "";
              const wc = text.trim() ? text.trim().split(/\s+/).length : 0;
              const hasPrev = activePartIndex > 0;
              const hasNext = activePartIndex < tests.length - 1;
              const totalParts = tests.length;

              return (
                <div className="flex flex-col h-full">
                  {/* Sub-header */}
                  <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3">
                    <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1 text-xs font-black text-white">
                        {partLabel} of {totalParts}
                      </span>
                      <p className="text-sm text-gray-700 font-medium">
                        {test.instruction ||
                          (!isWriting ? `Questions ${questionNums(test)[0] ?? 1} – ${questionNums(test).at(-1) ?? 10}` : test.title)}
                      </p>
                      {test.audio_url && (
                        <audio controls className="h-8 ml-auto" />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
                    {(isReading || isWriting) && (test.passage_text || test.image_url) ? (
                      <>
                        <div className="lg:w-1/2 p-5 overflow-y-auto overflow-x-hidden min-h-0">
                          {test.logo_url && (
                            <div className="flex justify-center mb-4">
                              <img src={test.logo_url} alt="" className="h-12 object-contain" />
                            </div>
                          )}
                          <h2 className="text-xl font-black text-gray-900 mb-3">
                            {test.passage_title || test.title}
                          </h2>
                          {test.passage_text && (
                            <div className="text-[15px] leading-8 text-gray-800 whitespace-pre-wrap mb-5">
                              {renderPassage(test.passage_text)}
                            </div>
                          )}
                          {test.image_url && (
                            <img src={test.image_url} alt="" className="rounded-xl border border-gray-200 object-contain max-h-96 w-full shadow-sm" />
                          )}
                          {test.image2_url && (
                            <img src={test.image2_url} alt="" className="rounded-xl border border-gray-200 object-contain max-h-96 w-full shadow-sm mt-3" />
                          )}
                          {test.image3_url && (
                            <img src={test.image3_url} alt="" className="rounded-xl border border-gray-200 object-contain max-h-96 w-full shadow-sm mt-3" />
                          )}
                          {Array.isArray(test.images) && test.images.map((img, i) => (
                            <img key={i} src={img} alt="" className="rounded-xl border border-gray-200 object-contain max-h-96 w-full shadow-sm mt-3" />
                          ))}
                        </div>
                        <div className="hidden lg:block w-px bg-gray-200 self-stretch" />
                        <div className="lg:w-1/2 p-5 overflow-y-auto overflow-x-hidden min-h-0 border-t lg:border-t-0 border-gray-200">
                          {isWriting ? (
                            <WritingPanel
                              test={test} writingText={text} setWritingText={(val) => setWritingTexts(p => ({ ...p, [test.id]: val }))}
                              isTimeUp={isTimeUp} submitting={submitting} wordCount={wc}
                            />
                          ) : (
                            <QuestionPanel test={test} answers={answers} updateAnswer={updateAnswer}
                              showAnswers={showAnswers} isTimeUp={isTimeUp} />
                          )}
                        </div>
                      </>
                    ) : isWriting ? (
                      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                        <div className="max-w-3xl mx-auto px-4 py-6">
                          <WritingPanel
                            test={test} writingText={text} setWritingText={(val) => setWritingTexts(p => ({ ...p, [test.id]: val }))}
                            isTimeUp={isTimeUp} submitting={submitting} wordCount={wc}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                        <div className="max-w-3xl mx-auto px-4 py-6">
                          {test.logo_url && (
                            <div className="flex justify-center mb-5">
                              <img src={test.logo_url} alt="" className="h-14 object-contain" />
                            </div>
                          )}
                          {test.image_url && (
                            <img src={test.image_url} alt="" className="mb-5 rounded-xl border border-gray-200 object-contain max-h-80 w-full shadow-sm" />
                          )}
                          {test.image2_url && (
                            <img src={test.image2_url} alt="" className="mb-5 rounded-xl border border-gray-200 object-contain max-h-80 w-full shadow-sm" />
                          )}
                          {test.image3_url && (
                            <img src={test.image3_url} alt="" className="mb-5 rounded-xl border border-gray-200 object-contain max-h-80 w-full shadow-sm" />
                          )}
                          {Array.isArray(test.images) && test.images.map((img, i) => (
                            <img key={i} src={img} alt="" className="mb-5 rounded-xl border border-gray-200 object-contain max-h-80 w-full shadow-sm" />
                          ))}
                          <QuestionPanel test={test} answers={answers} updateAnswer={updateAnswer}
                            showAnswers={showAnswers} isTimeUp={isTimeUp} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Navigation: Previous / Next */}
                  {!showAnswers && (
                    <div className="shrink-0 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                      <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <button
                          onClick={() => { setActivePartIndex(p => Math.max(0, p - 1)); window.scrollTo(0, 0); }}
                          disabled={!hasPrev}
                          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          ← Previous
                        </button>

                        {/* Part indicator dots */}
                        <div className="flex items-center gap-1.5">
                          {tests.map((_, i) => {
                            const answered = questionNums(tests[i]).filter(n => answers[n]?.trim()).length;
                            const total = questionNums(tests[i]).length;
                            const done = answered >= total;
                            return (
                              <button
                                key={i}
                                onClick={() => { setActivePartIndex(i); window.scrollTo(0, 0); }}
                                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${
                                  i === activePartIndex
                                    ? "bg-indigo-600 text-white scale-110"
                                    : done
                                    ? "bg-green-500 text-white"
                                    : "border-2 border-gray-300 text-gray-500 hover:border-indigo-300"
                                }`}
                              >
                                {i + 1}
                              </button>
                            );
                          })}
                        </div>

                        {hasNext ? (
                          <button
                            onClick={() => { setActivePartIndex(p => Math.min(tests.length - 1, p + 1)); window.scrollTo(0, 0); }}
                            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 transition-colors shadow-sm"
                          >
                            Next →
                          </button>
                        ) : isWriting ? (
                          <button
                            onClick={handleFinalSubmit}
                            disabled={submitting}
                            className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700 transition-colors shadow-md disabled:opacity-70"
                          >
                            {submitting ? (
                              <><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submit…</>
                            ) : (
                              <><CheckCircle className="h-4 w-4" /> Submit Exam</>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => setActiveSection(activeSection === 'listening' ? 'reading' : 'writing')}
                            disabled={submitting}
                            className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700 transition-colors shadow-sm"
                          >
                            {activeSection === 'listening' ? 'Go to Reading' : 'Go to Writing'} <ArrowRight className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </main>
        </>
      )}

      {/* ── Score Modal ── */}
      {showScoreModal && (
        <ScoreModal
          score={activeScore}
          onClose={() => setShowScoreModal(false)}
          onReview={() => setShowScoreModal(false)}
        />
      )}

      {/* ── Time-up modal ── */}

      {showTimeUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Clock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">Time is Up!</h2>
            <p className="text-gray-500 mb-6 text-sm">Your time for this section has finished.</p>
            {isWriting ? (
              <button
                onClick={() => { setShowTimeUpModal(false); handleFinalSubmit(); }}
                disabled={submitting}
                className="w-full bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-700 transition-colors text-sm"
              >
                {submitting ? "Submitting…" : "Submit Now"}
              </button>
            ) : (
              <button
                onClick={() => { setShowTimeUpModal(false); handleFinalSubmit(); }}
                disabled={submitting}
                className="w-full bg-gray-900 text-white font-bold py-3 rounded-xl hover:black transition-colors text-sm"
              >
                {submitting ? "Submitting…" : "Submit Now"}
              </button>
            )}
            <button onClick={() => setShowTimeUpModal(false)} className="mt-3 text-xs text-gray-400 hover:text-gray-600">
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IeltsPractice;
